<template>
  <Transition name="splash-out">
    <div v-if="isVisible" class="splash">
      <div class="splash__circle splash__circle--1" />
      <div class="splash__circle splash__circle--2" />

      <div class="splash__body" :class="{ 'splash__body--in': logoVisible }">
        <!-- Logo + ripple rings -->
        <div class="splash__logo-wrap">
          <span class="splash__ring splash__ring--1" />
          <span class="splash__ring splash__ring--2" />
          <img :src="logoUrl" alt="HelloYTa" class="splash__logo" />
        </div>

        <!-- Wave taglines -->
        <div class="splash__taglines">
          <p v-for="(line, lineIdx) in taglines" :key="lineIdx" class="splash__tagline">
            <span
              v-for="(char, charIdx) in splitChars(line)"
              :key="charIdx"
              class="splash__char"
              :style="{ animationDelay: `${lineIdx * 0.35 + charIdx * 0.045}s` }"
            >{{ char }}</span>
          </p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const STORAGE_KEY = 'helloyta_splash_shown';
const isVisible = ref(false);
const logoVisible = ref(false);
const logoUrl = `${import.meta.env.BASE_URL}img/logo-white-transparent.svg`;

const taglines = [
  'Chăm sóc sức khỏe và hỗ trợ y tế tận tâm',
  'Luôn sẵn sàng mọi thời điểm',
];

function splitChars(str) {
  return [...str].map(c => (c === ' ' ? ' ' : c));
}

onMounted(() => {
  if (window.innerWidth >= 960) return;
  if (localStorage.getItem(STORAGE_KEY)) return;

  isVisible.value = true;
  setTimeout(() => { logoVisible.value = true; }, 150);
  setTimeout(() => {
    isVisible.value = false;
    localStorage.setItem(STORAGE_KEY, '1');
  }, 3000);
});
</script>

<style lang="scss" scoped>
/* ── Base ── */
.splash {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background-color: var(--green);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* ── Background circles ── */
.splash__circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.07);
  animation: bg-pulse 2.6s ease-in-out infinite;

  &--1 {
    width: 380px;
    height: 380px;
    top: -100px;
    right: -100px;
  }
  &--2 {
    width: 280px;
    height: 280px;
    bottom: -70px;
    left: -70px;
    animation-delay: 1.3s;
  }
}

/* ── Body wrapper ── */
.splash__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0;
  transform: translateY(32px);
  transition: opacity 0.5s ease, transform 0.5s ease;

  &--in {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ── Logo ── */
.splash__logo-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
}

.splash__logo {
  width: 150px;
  position: relative;
  z-index: 1;
  animation: logo-bounce 0.85s cubic-bezier(0.36, 0.07, 0.19, 0.97) 0.15s both,
             logo-glow 2.4s ease-in-out 1.2s infinite;
}

.splash__ring {
  position: absolute;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);

  &--1 {
    width: 175px;
    height: 175px;
    animation: ring-expand 2.2s ease-out 0.7s infinite;
  }
  &--2 {
    width: 175px;
    height: 175px;
    animation: ring-expand 2.2s ease-out 1.45s infinite;
  }
}

/* ── Taglines ── */
.splash__taglines {
  text-align: center;
}

.splash__tagline {
  margin: 0 0 6px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.splash__char {
  display: inline-block;
  color: rgba(255, 255, 255);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  opacity: 0;
  animation: char-wave 0.5s ease forwards;
}

/* ── Exit transition ── */
.splash-out-leave-active {
  transition: opacity 0.55s ease, transform 0.55s ease;
}
.splash-out-leave-to {
  opacity: 0;
  transform: scale(1.06);
}

/* ── Keyframes ── */
@keyframes bg-pulse {
  0%, 100% { transform: scale(1);    opacity: 0.07; }
  50%       { transform: scale(1.1); opacity: 0.14; }
}

@keyframes logo-bounce {
  0%   { transform: scale(0.25);  opacity: 0; }
  55%  { transform: scale(1.2);   opacity: 1; }
  75%  { transform: scale(0.92); }
  90%  { transform: scale(1.06); }
  100% { transform: scale(1);    opacity: 1; }
}

@keyframes logo-glow {
  0%, 100% { filter: drop-shadow(0 0 6px  rgba(255,255,255,0.25)); }
  50%       { filter: drop-shadow(0 0 24px rgba(255,255,255,0.7));  }
}

@keyframes ring-expand {
  0%   { transform: scale(0.85); opacity: 0.65; }
  100% { transform: scale(2.1);  opacity: 0;    }
}

@keyframes char-wave {
  0%   { opacity: 0; transform: translateY(16px); }
  60%  { opacity: 1; transform: translateY(-4px);  }
  100% { opacity: 1; transform: translateY(0);     }
}
</style>
